import { useContext, useRef, useState, useEffect } from "react";
import WizardLayout from "../../../common/wizard-layout/wizard-layout";
import Footer from "../../../common/footer/footer";
import { Steps, getCurrentStepHeader, getCurrentStepSubHeader, stepDetails } from "../../target-steps";
import { WizardContext } from "../../../wizard-context";
import Header from "../../../common/header/header";
import Stepper from "../../../common/stepper/stepper";
import ValidationList, {
    SetAPIStatusProperty,
    ValidationListStatus
} from "../../../common/validation-list/validation-list";
import { checkTargetPrivileges, getEncryptedUrl, validateConnection } from "../../../services";
import metaobjectConverter from "../../../../../../core/services/metaStoreService/metaobject-converter";
import { getSourceConfig, getTargetConfig } from "../../../common/review/review.helper";
import { validationUtils, VALIDATION_TIMEOUT } from "../../../utils";
import { isAutomatedWizard } from "../../../../../../app/components/appwizard/component-wizards/wizards/source/database-reader/app-services";
import _ from "underscore";
import { saveHideURLStatus } from "../../../utils/encrypt-url-utils";
import Tracker from "../../../../../../core/services/tracker/tracker";
import { TRACKER_STRINGS } from "../../../../../../core/services/tracker/constants";

interface ValidateTargetProps {
    target: string;
}

const ValidateTarget = ({ target }: ValidateTargetProps) => {
    const [apiStatus, setAPIStatus] = useState<SetAPIStatusProperty>({
        isAPISuccessful: false,
        isAPICompleted: false,
        canRetry: false
    });

    const { setCurrentStep, targetInfo, sourceInfo, cdcSourceInfo, cdcTargetInfo, wizardType, templateID } = useContext(
        WizardContext
    );

    const validationListItems: React.ComponentProps<typeof ValidationList>["validationList"] = [
        {
            status: ValidationListStatus.Todo,
            text: "Trying to reach your target",
            method: function() {
                return validateConnection(target, targetInfo);
            }
        },
        {
            status: ValidationListStatus.Todo,
            text: "Validating Permissions",
            method: function() {
                return checkTargetPrivileges(target, targetInfo);
            }
        }
    ];

    const childRef = useRef();
    const heading = getCurrentStepHeader(Steps.ValidateTarget, target);
    const subHeading = getCurrentStepSubHeader(Steps.ValidateTarget);
    const stepper = <Stepper steps={stepDetails} activeStep={stepDetails.ValidateTarget.id} />;
    const getEncryptedURLs = async () => {
        const AppName = sourceInfo?.appId;
        const shortAppName = metaobjectConverter.convertFullNameToShortName(AppName);
        let sourceEncryptedURL = false,
            targetEncryptedURL = false;

        // Source
        const sourceData = sourceInfo.sourceAdapter.properties;
        let sourceDB =
            sourceData.adapterName === "DatabaseReader" ? sourceData.DatabaseProviderType : sourceData.adapterName;
        const sourceConfig = await getSourceConfig(shortAppName, sourceDB);
        const EncryptedSourceUrl = validationUtils.isRDBMS(sourceDB)
            ? await getEncryptedUrl(sourceDB, sourceConfig, shortAppName, sourceConfig.agentName)
            : null;
        // Target
        const targetDB = validationUtils.getTargetType(target);
        const targetConfig = await getTargetConfig(shortAppName, targetDB);
        const EncryptedTargetUrl = validationUtils.isRDBMS(targetDB)
            ? await getEncryptedUrl(targetDB, targetConfig, shortAppName)
            : null;
        try {
            const sourceURL = EncryptedSourceUrl;
            sourceEncryptedURL = _.isEmpty(sourceURL) ? null : sourceURL;
            saveHideURLStatus(shortAppName, _.isEmpty(sourceURL) ? null : true);
            const targetURL = EncryptedTargetUrl;
            targetEncryptedURL = _.isEmpty(targetURL) ? null : targetURL;
        } catch (e) {
            console.error(e);
        } finally {
            return {
                source: sourceEncryptedURL,
                target: targetEncryptedURL
            };
        }
    };

    useEffect(() => {
        const isAutomatedILCDC = isAutomatedWizard();
        if (isAutomatedILCDC) {
            getEncryptedURLs();
        }
    }, []);

    const trackEventClicked = buttonClicked => {
        const appId = isAutomatedWizard() ? cdcSourceInfo.appId : sourceInfo.appId;
        Tracker.getInstance().track(
            TRACKER_STRINGS.schema.wizards,
            {
                id: appId,
                event: TRACKER_STRINGS.eventClicked.wizards,
                step: Steps.ConfigureTarget,
                adapterType: target,
                buttonClicked: buttonClicked,
                isAutomatedILCDC: isAutomatedWizard()
            },
            TRACKER_STRINGS.version.withModification
        );
    };

    const handleNext = async () => {
        if (apiStatus.canRetry) {
            childRef?.current?.retryAPI();
        } else {
            setTimeout(async () => {
                setCurrentStep(Steps.Review);
            }, VALIDATION_TIMEOUT);
        }
        setAPIStatus(prevState => ({
            ...prevState,
            isAPISuccessful: false,
            isAPICompleted: false
        }));
        trackEventClicked("Next");
    };
    return (
        <WizardLayout
            Header={<Header heading={heading} subHeading={subHeading} />}
            Stepper={stepper}
            inlineDocKey={"validation-pages"}
            Footer={
                <Footer
                    nextDisabled={!apiStatus.isAPICompleted}
                    backDisabled={!apiStatus.isAPICompleted}
                    cancelDisabled={!apiStatus.isAPICompleted}
                    onNext={handleNext}
                    next={apiStatus.canRetry ? "Retry" : "Next"}
                    onBack={() => {
                        setCurrentStep(Steps.ConfigureTarget);
                        trackEventClicked("Back");
                    }}
                />
            }
        >
            <ValidationList
                validationList={validationListItems}
                setAPIStatus={setAPIStatus}
                ref={childRef}
                onSuccess={handleNext}
            />
        </WizardLayout>
    );
};

export default ValidateTarget;
